import { addEvent, updateEvent, updateAnimal, findAnimals, findEvents } from '../api/api';
import useTable from '../context/TableContext';
import useApp from '../context/AppContext';
import Date from '../functions';

const useEvent = () => {
    const { tableData, setTableData, tableActions } = useTable();
    const { setAnimals, setEvents, events } = useApp();

    const getEventInfo = (route, animals, events) => {
        let date = new Date().toDateInputValue();
        let notes = '';
        let event = null;
        let selectedAnimals = [];
        // Editing the animal
        if (route && route.animal_id && animals) {
            selectedAnimals = animals.filter(animal => parseInt(animal.id) === parseInt(route.animal_id));
        }
        // Editing the event
        else if (route && route.id && animals && events) {
            event = events.find(event => parseInt(event.id) === parseInt(route.id));
            if (event) {
                selectedAnimals = animals.filter(animal => parseInt(animal.id) === parseInt(event.animalId));
                date = event.dateApi;
                notes = event.notes;
            }
        }
        // Creating event for selected animals
        else {
            selectedAnimals = tableActions.animals.selectedRows ? animals.filter(animal => tableActions.animals.selectedRows.includes(animal.id)) : [];
        }

        return { selectedAnimals, date, notes, event };
    }

    const specialCase = (animal, formEvent, formAnimal, route) => {
        // If there is no event or if we are editing an animal, don't change anything
        if (!formEvent || (route && route.animal_id)) return formAnimal;
        let formAnimalCopy = JSON.parse(JSON.stringify(formAnimal));
        switch (parseInt(formEvent.type)) {
            case 2: {
                // if event type is marker don't replace markers but add only new markers
                // or delete markers if this is our choice
                const markers = [];
                animal.markers.map((marker) => {
                    let markerApi = '/api/markers/' + marker.id;
                    if (!formAnimalCopy.markers.includes(markerApi)) {
                        markers.push(markerApi);
                    }
                })
                if (formEvent.markerDelete) {
                    formAnimalCopy.markers = markers;
                } else {
                    formAnimalCopy.markers.push(...markers);
                }
                break;
            }   
            default:
        }
        return formAnimalCopy;
    }

    const getSortedEventsByDate = (events, sortOrder) => {
        return [...events].sort((a, b) => {
            if (a.dateApi === null || typeof a.dateApi == 'undefined') return 1;
            if (b.dateApi === null || b.dateApi == 'undefined') return -1;
            if ((a.dateApi === null && b.dateApi) || (typeof a.dateApi == 'undefined' && typeof b.dateApi == 'undefined')) return 0;
            return (
                a.dateApi.toString().localeCompare(b.dateApi.toString(), "en", {
                    numeric: true,
                }) * (sortOrder === "asc" ? 1 : -1)
            );
        });
    }

    const saveEvent = async (selectedAnimals, formEvent, formAnimal, route, refresh = true) => {
        const promiseAll = [];
        const messages = [];

        const addMessage = (type, message) => {
            messages.push({ type, message });
        }

        const hasErrors = (responses) => {
            let errors = [];
            responses.map(response => {
                if (response instanceof Promise) {
                    response.catch(err => {
                        errors.push('Erreur : ' + err);
                    });
                }
                if (response["@context"] && response["@context"] == '/api/contexts/Error') errors.push(response["hydra:description"]);
                if (response.code && response.code == 401) errors.push('vous n\'avez pas les droits pour modifier cet évènement.')
            });
            if (errors.length == responses.length) {
                addMessage('danger', 'Impossible d\'effectuer l\'enregistrement : ' + errors.join('; '));
            } else if (errors.length > 0) {
                addMessage('warning', 'Il y a eu des erreurs lors de l\'enregistrement : ' + errors.join('; '));
            } else {
                addMessage('success', responses.length + ' changement(s) effectué(s)');
            }
            if (errors.length > 0) return true;
            return false;
        }

        const refreshData = async (formEvent, formAnimal) => {
            const tableDataCopy = JSON.parse(JSON.stringify(tableData));
            const displayArchive = localStorage.getItem('displayArchive') && localStorage.getItem('displayArchive') == "true";
            let tableDataCopyModified = false;
            try {
                if (formEvent) {
                    await findEvents(displayArchive).then((events) => {
                        setEvents(events);
                        tableDataCopy.events = JSON.parse(JSON.stringify(events));
                        tableDataCopyModified = true;
                    });
                }
                if (formAnimal) {
                    await findAnimals(displayArchive).then((animals) => {
                        setAnimals(animals);
                        tableDataCopy.animals = JSON.parse(JSON.stringify(animals));
                        tableDataCopyModified = true;
                    });
                }
                if (tableDataCopyModified) {
                    setTableData(tableDataCopy);
                }
            }
            catch (err) {
                console.log(err);
                addMessage('warning', 'Il y a eu une erreur lors du rafraichissement des données');
            }
        }

        selectedAnimals.map((animal) => {
            // Update animal only if formAnimal exists 
            // and if it's the last event for this animal 
            // or it's a new event
            const animalEvents = events.filter(event => parseInt(animal.id) === parseInt(event.animalId));
            const eventsOfType = animalEvents.filter(event => parseInt(formEvent.type) === parseInt(event.type));
            const eventsSortedByDate = getSortedEventsByDate(eventsOfType, 'desc');
            const lastEvent = eventsOfType.length > 0 ? eventsSortedByDate[0] : null;
            if (formAnimal && (
                eventsOfType.length == 0 // is new event (there is not yet events of this type for this animal)
                || (lastEvent && formEvent.date >= lastEvent.dateApi) // is the last event
            )) {
                let formAnimalCopy = specialCase(animal, formEvent, formAnimal, route);
                promiseAll.push(updateAnimal(formAnimalCopy, animal.id));
            }
            if (formEvent) {
                const formEventCopy = {...formEvent, animal: "/api/animals/" + animal.id};
                if (route && route.id)
                    promiseAll.push(updateEvent(formEventCopy, route.id));
                else {
                    promiseAll.push(addEvent(formEventCopy));
                }
            }
        });

        return Promise.all(promiseAll)
            .catch(err => {
                console.log("error catched 1 : " + err);
                addMessage('warning', 'Il y a eu des erreurs lors de l\'enregistrement : ' + err.message);
            })
            .then(async responses => {
                try {
                    if (responses && responses.length > 0) {
                        if (!hasErrors(responses) && refresh) {
                            await refreshData(formEvent, formAnimal);
                        }
                    }
                }
                catch (err) {
                    addMessage('danger', 'Impossible d\'effectuer l\'enregistrement, erreur dans le code ?' + err.toString());
                    console.log("error catched 2 : ", err);
                }
                return messages;
            });
    }

    return { getEventInfo, saveEvent };
};

export default useEvent;